import { useEffect, useMemo, useState } from "react";
import type { BankQuestionDetail } from "../../shared/types";
import { Ico } from "./shell";
import { api } from "../api";
import { bankToDraft, type DraftQuestion } from "./QuestionForm";
import { AtlModalOverlay, AtlModalShell } from "./atelier";

const TYPE_LABEL: Record<string, string> = { mc: "MC", truefalse: "T/F", multi: "Multi" };

export default function BankPickerModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (questions: DraftQuestion[]) => void;
}) {
  const [bank, setBank] = useState<BankQuestionDetail[] | null>(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.listBank().then((r) => setBank(r.questions)).catch(() => setBank([]));
  }, []);

  const cats = useMemo(() => {
    const s = new Set<string>();
    for (const q of bank ?? []) s.add(q.category || "Uncategorised");
    return [...s];
  }, [bank]);

  const filtered = (bank ?? []).filter((q) => {
    if (cat && (q.category || "Uncategorised") !== cat) return false;
    if (query && !q.prompt.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function add() {
    const chosen = (bank ?? []).filter((q) => picked.has(q.id)).map(bankToDraft);
    if (chosen.length) onAdd(chosen);
    onClose();
  }

  return (
    <AtlModalOverlay onClose={onClose}>
      <AtlModalShell
        icon={<Ico name="bank" size={21} />}
        iconBg="var(--atl-gold-soft)"
        iconFg="var(--atl-gold)"
        title="Add from bank"
        sub="Pick saved questions to drop into this quiz"
        onClose={onClose}
        max={640}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 18 }}>
          <input
            className="atl-tf"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions…"
            style={{ width: "100%" }}
          />

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span
              className={cat === null ? "atl-chip atl-chip--accent" : "atl-chip"}
              style={{ cursor: "pointer" }}
              onClick={() => setCat(null)}
            >
              All
            </span>
            {cats.map((c) => (
              <span
                key={c}
                className={cat === c ? "atl-chip atl-chip--accent" : "atl-chip"}
                style={{ cursor: "pointer" }}
                onClick={() => setCat(c)}
              >
                {c}
              </span>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minHeight: 120,
              maxHeight: "48vh",
              overflowY: "auto",
              margin: "0 -4px",
              padding: "2px 4px",
            }}
          >
            {bank === null && (
              <div style={{ color: "var(--atl-faint)", fontWeight: 500, fontSize: 14, padding: "8px 2px" }}>
                Loading bank…
              </div>
            )}
            {bank !== null && filtered.length === 0 && (
              <div
                style={{
                  color: "var(--atl-soft)",
                  fontWeight: 500,
                  fontSize: 14,
                  padding: "24px 0",
                  textAlign: "center",
                }}
              >
                No matching questions in your bank.
              </div>
            )}
            {filtered.map((q) => {
              const on = picked.has(q.id);
              return (
                <div
                  key={q.id}
                  className="atl-row"
                  style={{
                    padding: "12px 16px",
                    gap: 13,
                    cursor: "pointer",
                    borderColor: on ? "var(--atl-c)" : undefined,
                  }}
                  onClick={() => toggle(q.id)}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      background: on ? "var(--atl-c)" : "transparent",
                      color: "#fff",
                      border: on ? "none" : "1.5px solid var(--atl-line)",
                    }}
                  >
                    {on && <Ico name="check" size={12} />}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 500,
                      fontSize: 14.5,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {q.prompt || "Untitled question"}
                  </span>
                  <span
                    className="atl-mono"
                    style={{
                      fontSize: 11,
                      padding: "4px 10px",
                      borderRadius: 99,
                      border: "1px solid var(--atl-line)",
                      color: "var(--atl-soft)",
                    }}
                  >
                    {TYPE_LABEL[q.type] ?? q.type}
                  </span>
                  <span
                    className="atl-mono"
                    style={{ fontSize: 11.5, fontWeight: 700, color: "var(--atl-faint)" }}
                  >
                    {q.timeLimit}s
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button className="atl-btn atl-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="atl-btn atl-btn--primary" disabled={picked.size === 0} onClick={add}>
              <Ico name="plus" size={15} /> Add {picked.size || ""} to quiz
            </button>
          </div>
        </div>
      </AtlModalShell>
    </AtlModalOverlay>
  );
}
